<script setup lang="ts">
import { onMounted } from 'vue'
import { NButton, NEmpty, NPopconfirm, NSpin, NTime, useMessage } from 'naive-ui'
import { RouterLink } from 'vue-router'
import RouteShell from './RouteShell.vue'
import { useSharesStore } from '../stores/shares'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Admin-only page listing currently-active public share links so
// the owner always knows what's live and can revoke any one early. New
// links are created from a page's Share action, not here — this surface
// is the "what's live right now / kill it" safety net. The link URL is
// never shown again (only its digest is stored), so the list shows page
// + expiry, not the link. Auth gating is router-level.

const store = useSharesStore()
const message = useMessage()

useDocumentTitle('Shares')

onMounted(async () => {
  try {
    await store.fetchAll()
  } catch {
    // loadError surfaces in the template.
  }
})

async function revoke(id: number) {
  try {
    await store.revoke(id)
    message.success('Share revoked')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to revoke share')
  }
}
</script>

<template>
  <RouteShell>
    <section class="max-w-3xl mx-auto flex flex-col gap-4 pb-6">
      <header class="flex flex-col gap-1">
        <h1 class="text-2xl font-semibold text-white m-0">Shares</h1>
        <p class="text-sm text-white/60 m-0">
          Active public share links. Each renders one page at a temporary
          <code class="font-mono text-xs">/s/…</code> URL until it expires. Revoke one to cut off access immediately.
          Create new links from a page's <strong>Share</strong> action.
        </p>
      </header>

      <div v-if="!store.loaded" class="flex items-center justify-center py-12">
        <NSpin />
      </div>
      <NEmpty v-else-if="store.shares.length === 0" description="No active share links." />
      <ul v-else class="flex flex-col gap-2 list-none p-0 m-0" data-testid="share-list">
        <li
          v-for="share in store.shares"
          :key="share.id"
          class="flex items-center justify-between gap-3 rounded border border-white/10 bg-black/20 px-3 py-2"
        >
          <div class="flex flex-col gap-0.5 min-w-0">
            <RouterLink
              v-if="share.page_path"
              :to="`/p/${share.page_path}`"
              class="text-sm text-white font-medium truncate no-underline hover:underline"
              >{{ share.page_title || share.page_path }}</RouterLink
            >
            <div class="text-xs text-white/50 truncate">
              <span v-if="share.expires_at">
                expires <NTime :time="new Date(share.expires_at)" type="relative" />
              </span>
              · created <NTime :time="new Date(share.created_at)" type="relative" />
            </div>
          </div>
          <NPopconfirm @positive-click="revoke(share.id)">
            <template #trigger>
              <NButton size="small" tertiary type="error" :data-testid="`share-revoke-${share.id}`"> Revoke </NButton>
            </template>
            Revoke this share? The link will stop working immediately.
          </NPopconfirm>
        </li>
      </ul>
    </section>
  </RouteShell>
</template>
